// DetailsCard.tsx — rename, notes and tags for a scan. Single-line fields are
// the Axios Text primitive; notes are a dashed-transparent textarea
// (C-INPUT-3). Save PATCHes only what changed. A capture lit by the torch
// (§16) is noted with its exposure summary under the fields. A viewer sees the
// details as text; pinning is the owner's (it orders the owner's library).
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { BsPinAngle, BsPinAngleFill, BsSave } from 'react-icons/bs';
import { ui } from 'common';
import { api, atLeast } from '../api';
import type { Scan } from '../api';
import { errorMessage, parseTags } from '../format';

const { Text } = ui;

export interface DetailsCardProps {
  scan: Scan;
  onScan: (s: Scan) => void;
  onError: (msg: string) => void;
}

export function DetailsCard({ scan, onScan, onError }: DetailsCardProps) {
  const [name, setName] = useState(scan.name);
  const [notes, setNotes] = useState(scan.notes);
  const [tags, setTags] = useState(scan.tags.join(', '));
  const [saved, setSaved] = useState('');

  // Re-seed the form when a different scan (or a server update) arrives.
  const serverTags = scan.tags.join(', ');
  useEffect(() => {
    setName(scan.name);
    setNotes(scan.notes);
    setTags(serverTags);
  }, [scan.id, scan.name, scan.notes, serverTags]);

  const editor = atLeast(scan, 'editor');
  const owner = atLeast(scan, 'owner');
  const nextTags = parseTags(tags);
  const dirty = name.trim() !== scan.name || notes !== scan.notes || nextTags.join(',') !== scan.tags.join(',');

  const save = async () => {
    const patch: Partial<Pick<Scan, 'name' | 'notes' | 'tags'>> = {};
    if (name.trim() && name.trim() !== scan.name) patch.name = name.trim();
    if (notes !== scan.notes) patch.notes = notes;
    if (nextTags.join(',') !== scan.tags.join(',')) patch.tags = nextTags;
    try {
      onScan(await api.patchScan(scan.id, patch));
      setSaved('Saved.');
      console.info('[openscan/viewer] details saved', Object.keys(patch));
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  const togglePin = async () => {
    try {
      onScan(await api.patchScan(scan.id, { pinned: !scan.pinned }));
    } catch (err) {
      onError(errorMessage(err));
    }
  };

  const exposureNote = (scan.flash_used || scan.exposure) && (
    <div className="small text-body-secondary mt-2">
      {scan.flash_used ? 'Flash used' : 'No flash'}
      {scan.exposure && ` · exposure ${scan.exposure.locked ? 'locked' : 'auto'}, gain range ${scan.exposure.gain_range_ev.toFixed(2)} EV`}
    </div>
  );

  if (!editor) {
    return (
      <Card>
        <Card.Header>Details</Card.Header>
        <Card.Body>
          <div className="small text-body-secondary">Owner</div>
          <div className="mb-2 text-break">{scan.owner}</div>
          {scan.notes && <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{scan.notes}</p>}
          {scan.tags.length > 0 && <div className="small">{scan.tags.map((t) => `#${t}`).join(' ')}</div>}
          {exposureNote}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>Details</Card.Header>
      <Card.Body>
        <Form.Group className="mb-2" controlId="scan-name">
          <Form.Label className="small mb-1">Name</Form.Label>
          <Text value={name} onChange={(e) => { setName(e.target.value); setSaved(''); }} style={{ width: '100%' }} />
        </Form.Group>
        <Form.Group className="mb-2" controlId="scan-notes">
          <Form.Label className="small mb-1">Notes</Form.Label>
          <Form.Control as="textarea" rows={3} value={notes} onChange={(e) => { setNotes(e.target.value); setSaved(''); }} />
        </Form.Group>
        <Form.Group controlId="scan-tags">
          <Form.Label className="small mb-1">Tags (comma-separated)</Form.Label>
          <Text value={tags} onChange={(e) => { setTags(e.target.value); setSaved(''); }} style={{ width: '100%' }} />
        </Form.Group>
        {exposureNote}
      </Card.Body>
      <Card.Footer className="d-flex align-items-center gap-2">
        <Button size="sm" variant="primary" disabled={!dirty || !name.trim()} onClick={() => void save()}>
          <BsSave className="me-1" />
          Save
        </Button>
        {owner && (
          <Button size="sm" variant="link" onClick={() => void togglePin()}>
            {scan.pinned ? <BsPinAngleFill className="me-1" /> : <BsPinAngle className="me-1" />}
            {scan.pinned ? 'Unpin' : 'Pin'}
          </Button>
        )}
        <span className="small text-body-secondary ms-auto">{saved}</span>
      </Card.Footer>
    </Card>
  );
}
